import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Play } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../store/lang";

const GAME_SECONDS = 30;

export const StarlightCatcher = () => {
  const { ur } = useLang();
  const areaRef = useRef(null);
  const basketX = useRef(50);
  const caughtIds = useRef(new Set());
  const [running, setRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(GAME_SECONDS);
  const [score, setScore] = useState(0);
  const [stars, setStars] = useState([]);
  const [height, setHeight] = useState(380);

  const start = () => {
    const h = areaRef.current?.clientHeight || 380;
    setHeight(h);
    caughtIds.current = new Set();
    setStars([]);
    setScore(0);
    setTimeLeft(GAME_SECONDS);
    setRunning(true);
  };

  useEffect(() => {
    if (!running) return;
    const spawn = setInterval(() => {
      setStars((s) => [
        ...s,
        { id: Math.random().toString(36).slice(2), x: 6 + Math.random() * 88, dur: 2.2 + Math.random() * 1.8 },
      ]);
    }, 620);
    const clock = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          setRunning(false);
          setStars([]);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => {
      clearInterval(spawn);
      clearInterval(clock);
    };
  }, [running]);

  useEffect(() => {
    if (!running && timeLeft === 0 && score > 0) {
      toast.success(ur ? `${score} ستارے ہایا کے لیے جمع ہوئے!` : `${score} stardust gathered for Haya!`);
    }
  }, [running, timeLeft, score]);

  const moveBasket = (clientX) => {
    const rect = areaRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    basketX.current = Math.min(94, Math.max(6, pct));
    const basket = areaRef.current.querySelector("[data-basket]");
    if (basket) basket.style.left = `${basketX.current}%`;
  };

  const catchStar = (id) => {
    if (caughtIds.current.has(id)) return;
    caughtIds.current.add(id);
    setScore((s) => s + 1);
    setStars((s) => s.filter((st) => st.id !== id));
  };

  return (
    <div data-testid="starlight-catcher-game">
      <div className="flex items-center justify-between mb-4 text-sm">
        <span className="text-amber-100" dir={ur ? "rtl" : "ltr"}>
          {ur ? "ستارے:" : "Stardust:"} <span data-testid="starlight-score" className="font-royal text-xl gold-text">{score}</span>
        </span>
        <span className="text-slate-400 tabular-nums">
          {running ? (ur ? `${timeLeft} سیکنڈ باقی` : `${timeLeft}s remaining`) : ur ? "30 سیکنڈ کا جادو" : "30 seconds of magic"}
        </span>
      </div>

      <div
        ref={areaRef}
        data-testid="starlight-game-canvas"
        onMouseMove={(e) => moveBasket(e.clientX)}
        onTouchMove={(e) => moveBasket(e.touches[0].clientX)}
        className="relative h-[380px] rounded-2xl overflow-hidden bg-gradient-to-b from-[#0a0c1e] to-[#19102e] border border-amber-200/15 touch-none select-none cursor-none"
      >
        {!running && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 bg-[#070814]/60 backdrop-blur-sm">
            <p className="text-slate-300 font-light text-center px-6 max-w-sm text-sm" dir={ur ? "rtl" : "ltr"}>
              {ur
                ? "مخملی ٹوکری ہلائیں اور گرتے ستارے پکڑیں — ہر ستارہ ہایا کے لیے ایک دعا ہے۔"
                : "Move the velvet basket to catch falling stardust — every star is a blessing for Haya."}
            </p>
            <button data-testid="starlight-game-start-btn" onClick={start} className="btn-gold">
              <Play size={15} /> {score > 0 ? (ur ? "دوبارہ کھیلیں" : "Play Again") : ur ? "کھیل شروع کریں" : "Begin the Magic"}
            </button>
          </div>
        )}

        <AnimatePresence>
          {stars.map((star) => (
            <motion.div
              key={star.id}
              initial={{ y: -40, opacity: 1 }}
              animate={{ y: height + 50 }}
              exit={{ scale: 1.8, opacity: 0, transition: { duration: 0.25 } }}
              transition={{ duration: star.dur, ease: "linear" }}
              onUpdate={(latest) => {
                if (
                  latest.y > height - 86 &&
                  latest.y < height - 34 &&
                  Math.abs(star.x - basketX.current) < 8
                ) {
                  catchStar(star.id);
                }
              }}
              onAnimationComplete={() => setStars((s) => s.filter((st) => st.id !== star.id))}
              className="absolute top-0"
              style={{ left: `${star.x}%` }}
            >
              <Star size={26} className="text-amber-200 fill-amber-300 drop-shadow-[0_0_10px_rgba(243,229,171,0.9)]" />
            </motion.div>
          ))}
        </AnimatePresence>

        <div
          data-basket
          className="absolute bottom-3 -translate-x-1/2 w-20 h-10 rounded-b-3xl rounded-t-lg bg-gradient-to-b from-[#3b2a63] to-[#241640] border border-amber-200/40 shadow-[0_0_20px_rgba(120,90,200,0.4)]"
          style={{ left: "50%" }}
        >
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-24 h-3 rounded-full bg-[#19102e] border border-amber-200/40" />
        </div>
      </div>
    </div>
  );
};
